<template>
  <div class="relative">
    <nav class="flex flex-col w-screen z-10">
      <div class="flex flex-row justify-between text-sm md:text-2xl h-16
        w-full mx-0 md:mx-auto bg-white px-0 fixed border z-50">
        <router-link to="/" class="flex items-center">
          <img src="../images/Logo.jpg" class="w-40 md:w-64">
        </router-link>
        <div id="nav"
          class="flex w-auto justify-between items-center space-x-2 md:space-x-10">
          <router-link to="/signin" class="items-center mr-2 text-sm text-orange-academy-300
            uppercase hidden md:flex h-full font-bold"
            v-if="!authenticated && !$route.fullPath.includes('signin')
            && $route.fullPath !== '/registration'">
              {{ $t('header.login') }}
          </router-link>
          <div class="flex w-0 md:w-full h-full items-center mr-6 md:mr-0"
            :class="containerColors"
            v-if="authenticated">
            <md-menu md-size="medium" md-align-trigger class="z-10 w-full">
              <md-button md-menu-trigger>
                <div class="flex items-center px-3">
                  <span class="text-xs md:text-sm font-medium text-white
                  uppercase mr-0 md:mr-3 hidden md:flex">
                  {{ user.student ? user.student.firstname : user.name }}</span>
                  <img :src="user.urlProfilePhoto ? user.urlProfilePhoto : 'https://baroon.fra1.digitaloceanspaces.com/dev/academy/users/UserIcon.png'"
                    class="rounded-full w-7 md:w-8 transform
                    group-hover:scale-105" v-if="!isAdmin">
                </div>
              </md-button>
              <md-menu-content class="bg-white backdrop-filter backdrop-blur-lg
              backdrop-opacity-10 z-10">
                <md-menu-item  @click="goToDashboard" class="hover:bg-beige-academy-100 font-light
                font-sans-serif">
                  {{ $t('header.profile') }}
                </md-menu-item>
                <md-menu-item  @click="goTo('messages')" class="hover:bg-beige-academy-100
                flex justify-between w-full items-center font-light font-sans-serif">
                    {{ $t('header.messages') }}
                  <span class="rounded-full px-2 py-1 text-xs text-white"
                  v-if="messagesToRead.length>0"
                  :class="isTeacher? 'bg-beige-academy-300' : 'bg-orange-academy-300'">
                    {{ messagesToRead.length }}
                  </span>
                </md-menu-item>
                <md-menu-item  @click="logout" class="hover:bg-beige-academy-100 font-light
                font-sans-serif">
                  {{ $t('header.logout') }}</md-menu-item>
              </md-menu-content>
            </md-menu>
          </div>

          <button class="ml-2 flex items-center uppercase text-xs md:text-sm pr-4 lg:pr-24"
            @click="$emit('input', !value)">
            <span class="hidden lg:block w-5 opacity-70 bgColor">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><!--! Font Awesome Pro 6.1.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M0 96C0 78.33 14.33 64 32 64H416C433.7 64 448 78.33 448 96C448 113.7 433.7 128 416 128H32C14.33 128 0 113.7 0 96zM0 256C0 238.3 14.33 224 32 224H416C433.7 224 448 238.3 448 256C448 273.7 433.7 288 416 288H32C14.33 288 0 273.7 0 256zM416 448H32C14.33 448 0 433.7 0 416C0 398.3 14.33 384 32 384H416C433.7 384 448 398.3 448 416C448 433.7 433.7 448 416 448z"/></svg>
            </span>
          </button>

          <profile-notifications class="z-20" v-if="showNotificationsBar">
          </profile-notifications>
          <button  v-if="authenticated && $route.fullPath !== '/signin'
            && $route.fullPath !== '/registration'" class="flex flex-row right-4
            absolute" @click="toggleNotifications">
            <notification-icon class="text-orange-academy-300 flex items-center z-10"
            v-if="!showNotificationsBar"/>
            <close-icon class="flex w-8 p-3 text-black items-center z-40 fixed
            right-1"
              v-if="showNotificationsBar"></close-icon>
          </button>

          <profile-messages class="z-20" v-if="messages"></profile-messages>
          <button class="flex flex-row right-2
            absolute" @click="openMessages()"
            v-if="authenticated && $route.fullPath !== '/signin'
            && $route.fullPath !== '/registration'" >
            <message-icon class="text-orange-academy-300 w-5 flex items-center z-10"
            v-if="messages === false"/>
            <close-icon class="flex w-8 p-3 text-black items-center z-40 fixed
            right-2"
              v-if="messages === true"></close-icon>
          </button>
        </div>
      </div>
    </nav>
    <modal name="notification" :adaptive="true" :scrollable="true">
      <keep-alive>
        <component :is="componentToOpen" :notification="selectedNotification" />
      </keep-alive>
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import StreamFunctionsMixin from '@/mixins/StreamFunctionsMixin';
import dayjs from 'dayjs';
import NotificationIcon from '@/components/graphics/NotificationIcon.vue';
import AcceptNewLessonTime from '@/components/modals/AcceptNewLessonTime.vue';
import StudentRejectsLesson from '@/components/modals/StudentRejectsLesson.vue';
import MessageIcon from '@/components/graphics/MessageIcon.vue';
import ProfileMessages from '@/components/graphics/ProfileMessages.vue';
import CloseIcon from '@/components/graphics/CloseIcon.vue';
import ProfileNotifications from '@/components/graphics/ProfileNotifications.vue';

export default {
  props: ['value'],

  components: {
    NotificationIcon,
    AcceptNewLessonTime,
    StudentRejectsLesson,
    MessageIcon,
    ProfileMessages,
    CloseIcon,
    ProfileNotifications,
  },

  data() {
    return {
      componentToOpen: '',
      selectedNotification: null,
      messages: false,
      notifications: false,
    };
  },

  watch: {
    authenticated() {
      if (!this.authenticated) {
        this.setChoosenTimezone(this.localTimezone);
        dayjs.tz.setDefault(this.choosenTimezone);
      }
    },
  },

  computed: {
    ...mapGetters({
      authenticated: 'auth/authenticated',
      user: 'auth/user',
      messagesToRead: 'auth/messagesToRead',
      notificationsToRead: 'notifications/notificationsToRead',
      isTeacher: 'auth/isTeacher',
      isAdmin: 'auth/isAdmin',
      showNotificationsBar: 'showNotificationsBar',
    }),

    containerColors() {
      if (this.user && this.isTeacher) {
        return 'bg-beige-academy-300 h-20';
      }
      return 'bg-orange-academy-300 h-20';
    },

    textColor() {
      if (this.isTeacher) {
        return 'beige-academy-300';
      }
      return 'orange-academy-300';
    },
  },

  methods: {
    ...mapActions({
      signOut: 'auth/SignOut',
      setChoosenTimezone: 'timezone/setChoosenTimezone',
      toggleNotifications: 'toggleNotifications',
    }),
    logout() {
      this.signOut().then(() => {
        this.createNotification('info', this.$t('notifications.logout'));
        this.$router.push({ path: '/' }).catch(() => {});
      });
    },

    goTo(path) {
      this.$router.push(this.isTeacher ? `/teacher-dashboard/${path}` : `/student-dashboard/${path}`);
    },

    openMessages() {
      this.messages = !this.messages;
    },
  },

  mixins: [StreamFunctionsMixin],

};
</script>

<style scoped>
  .rotated {
    transform:scale(-1, 1);
    -ms-transform:scale(-1, 1);
    -webkit-transform:scale(-1, 1);
  }

  .bgColor {
    stroke: rgb(38, 36, 75);
    fill: rgb(38, 36, 75);
  }
</style>
